/**
 * Gestione condivisioni casella (deleghe): il proprietario condivide una propria
 * casella con un altro utente Blagomail, in sola lettura o con permesso di invio.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Plus, X, Loader2, Send, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { accountsApi, sharesApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';

export function ShareManager() {
  const qc = useQueryClient();
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => accountsApi.list() });
  const ownedAccounts = useMemo(
    () => (accountsQuery.data?.accounts ?? []).filter((a) => !a.shared),
    [accountsQuery.data]
  );

  const [accountId, setAccountId] = useState<string>('');
  const selected = accountId || ownedAccounts[0]?.id || '';

  const [email, setEmail] = useState('');
  const [canSend, setCanSend] = useState(false);

  const sharesQuery = useQuery({
    queryKey: ['shares', selected],
    queryFn: () => sharesApi.list(selected),
    enabled: !!selected,
  });
  const shares = sharesQuery.data?.shares ?? [];

  const addMut = useMutation({
    mutationFn: () => sharesApi.create(selected, email.trim(), canSend),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shares', selected] });
      setEmail(''); setCanSend(false);
      toast.success('Casella condivisa');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Condivisione fallita'),
  });

  const removeMut = useMutation({
    mutationFn: (shareId: string) => sharesApi.remove(selected, shareId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shares', selected] }); toast.success('Condivisione revocata'); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Errore'),
  });

  if (ownedAccounts.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Caselle condivise</CardTitle>
        <CardDescription>
          Concedi a un altro utente Blagomail l'accesso a una tua casella (sola lettura o con invio).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {ownedAccounts.length > 1 && (
          <div className="space-y-1.5">
            <Label>Casella da condividere</Label>
            <select
              value={selected}
              onChange={(e) => setAccountId(e.target.value)}
              className="w-full text-sm rounded-md border bg-background px-3 py-2 outline-none focus:ring-1 focus:ring-primary"
            >
              {ownedAccounts.map((a) => (
                <option key={a.id} value={a.id}>{a.email_address}</option>
              ))}
            </select>
          </div>
        )}

        {/* condivisioni attuali */}
        <div className="space-y-2">
          {sharesQuery.isLoading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : shares.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessuna condivisione attiva su questa casella.</p>
          ) : (
            shares.map((s) => (
              <div key={s.id} className="flex items-center gap-3 rounded-md border p-2.5">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{s.delegate_name || s.delegate_email}</div>
                  <div className="text-xs text-muted-foreground truncate">{s.delegate_email}</div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs rounded-full bg-muted px-2 py-0.5">
                  {s.can_send ? <><Send className="h-3 w-3" /> Invio</> : <><Eye className="h-3 w-3" /> Lettura</>}
                </span>
                <button
                  type="button"
                  onClick={() => removeMut.mutate(s.id)}
                  className="text-muted-foreground hover:text-destructive"
                  title="Revoca"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* aggiungi condivisione */}
        <div className="rounded-md border p-3 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="share-email">Email dell'utente Blagomail</Label>
            <Input id="share-email" type="email" placeholder="collega@azienda.it"
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="share-cansend" className="text-sm font-normal cursor-pointer">Può inviare email da questa casella</Label>
            <Switch id="share-cansend" checked={canSend} onCheckedChange={setCanSend} />
          </div>
          <Button onClick={() => addMut.mutate()} disabled={!email.trim() || !selected || addMut.isPending} className="w-full">
            {addMut.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
            Condividi casella
          </Button>
          <p className="text-[11px] text-muted-foreground">
            L'utente deve già avere un account Blagomail. In <strong>sola lettura</strong> può solo leggere la casella.
            Con <strong>invio</strong> può anche rispondere a suo nome e gestirla (archiviare, cestinare, etichettare).
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
